import { useCallback, useRef } from 'react';
import { PlanningEntityType } from 'generated';
import { useSessionStorage } from 'usehooks-ts';
import { View } from '../enums';
import { isJSONString } from '../utils';

export type PlanningEntityTypePreference = {
  [key in View]: PlanningEntityType;
};

export default function usePlanningEntity(): [
  PlanningEntityTypePreference,
  (pref: PlanningEntityTypePreference) => void
] {
  const [planningEntityType, setPlanningEntityType] = useSessionStorage(
    'activityPlanningPlanningEntityType',
    'WORKPLACE'
  );

  // Read the latest raw value via ref so `setPlanningEntity` never needs to
  // close over `planningEntityType` itself. This keeps the returned setter
  // referentially stable across renders (only changes when `setPlanningEntityType`
  // from `useSessionStorage` changes, which is effectively never).
  // Consumers can therefore safely include the setter in `useEffect` and
  // `useMemo` dependency arrays without triggering extra runs on every write.
  const planningEntityTypeRef = useRef(planningEntityType);
  planningEntityTypeRef.current = planningEntityType;

  const setPlanningEntity = useCallback(
    (planningEntity: PlanningEntityTypePreference) => {
      const currentPlanningEntity = isJSONString(planningEntityTypeRef.current)
        ? (JSON.parse(
            planningEntityTypeRef.current
          ) as PlanningEntityTypePreference)
        : {};
      setPlanningEntityType(
        JSON.stringify({
          ...currentPlanningEntity,
          ...planningEntity
        })
      );
    },
    [setPlanningEntityType]
  );

  return [
    isJSONString(planningEntityType) ? JSON.parse(planningEntityType) : {},
    setPlanningEntity
  ];
}
